// src/main.tsx
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import App from './App';
import { ToastContainer } from './components/ToastContainer';
import { showToast } from './lib/toast';
import { ApiError } from './lib/api';
import './styles/global.css';

const queryClient = new QueryClient({
  // Global mutation error handler — specifically surfaces rate-limit
  // (429) responses as a toast, design confirmed 2026-07-16. Hooked in
  // once here rather than added to every individual mutation across
  // the app, since this is a cross-cutting concern (any write action
  // anywhere could get rate-limited), not something specific to one
  // form.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 429) {
        showToast(error.message);
      }
    },
  }),
  defaultOptions: {
    queries: {
      // Dashboard data (bot health, commands, etc.) shouldn't be
      // treated as instantly stale the moment it's fetched — avoids
      // needless refetch storms on every tab focus for data that
      // only actually changes every few minutes at most.
      staleTime: 30_000,
      retry: 1,
      // Also disabled outright, not just relied on staleTime: default
      // TanStack Query behavior refetches on every window focus
      // regardless of staleTime for some query configurations, and
      // combined with the rate limiter's IP-keyed bucket for routes
      // with no guildId (like GET /api/guilds), repeated tab-switching
      // during normal use was a real contributing factor to exhausting
      // that shared budget (found 2026-07-12).
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ToastContainer />
    </QueryClientProvider>
  </StrictMode>,
);
